import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import { Icon, ToolTipWrapper, TOP_TIP_POSITION } from '@src/quince-components/src'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import ProductComparisonBrand, {
  type PCBAdditionalProperties,
} from '@src/quince-site-common/src/domains/ProductComparisonBrand'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import {
  FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME,
  MF_PC_ADDITONAL_ATTRIBUTE_NAME,
  OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME,
  PPS_PC_ADDITONAL_ATTRIBUTE_NAME,
  SERVING_PC_ADDITONAL_ATTRIBUTE_NAME,
  WGD_PC_ADDITONAL_ATTRIBUTE_NAME,
} from '@src/quince-site-common/src/utils/constants'
import IButtonV2 from '@src/quince-site-integrated/src/assets/info_white.svg'
import AdditionalPropertyComponent from '@src/quince-site-pdp/src/molecules/productPriceComparision/AdditionalPropertyComponent'
import ComparisonRow from '@src/quince-site-pdp/src/molecules/productPriceComparision/ComparisonRow'
import CrossIcon from '@src/quince-site-pdp/src/molecules/productPriceComparision/icons/cross.inline.svg'
import GrayCircle from '@src/quince-site-pdp/src/molecules/productPriceComparision/icons/gray-circle.inline.svg'
import YellowCircle from '@src/quince-site-pdp/src/molecules/productPriceComparision/icons/yellow-circle.inline.svg'
import {
  getBrandsExtraPrice,
  getVitaminSavingsPercentage,
} from '@src/quince-site-pdp/src/molecules/productPriceComparision/productPriceComparision.helper'
import styles from '@src/quince-site-pdp/src/molecules/productPriceComparision/productPriceComparision.module.scss'
import { useProductComparisonTooltip } from '@src/quince-site-pdp/src/molecules/productPriceComparision/productPriceComparision.tooltip'
import { getDisplayKey } from '@src/utils/i18n.utils'

interface ProductPriceComparisionProps {
  quincePrice: number
  productComparisionsBrands?: ProductComparisonBrand[]
  hideFreeReturns?: boolean
  showDollarComparisonPrice?: boolean
  hasRestockingFee?: boolean
  showGiftCardCredit?: boolean
  giftCardInformation?: Partial<{
    giftCardCredit: number
    productComparisionsBrands: ProductComparisonBrand[]
  }>
  servings?: number
  pricePerServing?: number
  hideSavings?: boolean
  isDogFood?: boolean
  showPricePerUnit?: boolean
}

export const YellowCircleContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__yellowCircleWrapper}>
      <span className="sr-only">{t('yes')}</span>
      <Icon iconSvg={YellowCircle} className={styles.circleIcon} />
    </div>
  )
}

export const GrayCircleContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__grayCircleWrapper}>
      <span className="sr-only">{t('yes')}</span>
      <Icon iconSvg={GrayCircle} className={styles.circleIcon} />
    </div>
  )
}

export const CrossIconContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__crossIconWrapper}>
      <span className="sr-only">{t('no')}</span>
      <Icon iconSvg={CrossIcon} className={styles.crossIcon} />
    </div>
  )
}

const IButtonIconContainer = () => (
  <span className={styles.productPriceComparision__bodyRow__cell__iButtonIconWrapper}>
    <Icon iconSvg={IButtonV2} className={styles.iButtonIcon} />
  </span>
)

const ProductPriceComparision = ({
  quincePrice,
  productComparisionsBrands,
  hideFreeReturns = false,
  showDollarComparisonPrice,
  hasRestockingFee,
  showGiftCardCredit,
  giftCardInformation,
  servings,
  pricePerServing,
  hideSavings,
  isDogFood,
  showPricePerUnit,
}: ProductPriceComparisionProps) => {
  const { deviceType } = useDeviceInfo()

  const { t } = useTranslation('pdp')
  const { t: tCommon } = useTranslation('common')
  const isNewLocalisedFlow = useI18nPreviewContext()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const tooltip = useProductComparisonTooltip(productComparisionsBrands ?? [])

  productComparisionsBrands?.sort(
    (brandOne, brandTwo) => (brandOne.prices[0]?.price ?? 0) - (brandTwo.prices[0]?.price ?? 0),
  )

  const additionalProps: PCBAdditionalProperties[] = productComparisionsBrands?.[0]?.additionalProperties ?? []
  const propsMap = additionalProps.reduce<Record<string, PCBAdditionalProperties>>((acc, item) => {
    if (item.key) {
      acc[item.key] = item
    }
    return acc
  }, {})

  const whiteGloveDeliveryAttribute = propsMap[WGD_PC_ADDITONAL_ATTRIBUTE_NAME]
  const membershipFeeAttribute = propsMap[MF_PC_ADDITONAL_ATTRIBUTE_NAME]
  const furnitureCoversAttribute = propsMap[FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME]
  const servingsAttribute = propsMap[SERVING_PC_ADDITONAL_ATTRIBUTE_NAME]
  const pricePerServingAttribute = propsMap[PPS_PC_ADDITONAL_ATTRIBUTE_NAME]
  const overnightShippingAttribute = propsMap[OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME]

  const effectiveQuincePrice = quincePrice - (giftCardInformation?.giftCardCredit ?? 0)
  return (
    <div className={classNames(styles.productPriceComparision, styles.productPriceComparision_newDesignV2)}>
      <div className={styles.productPriceComparision__titleWrapper}>
        <span className={styles.productPriceComparision__titleWrapper__title}>{t('beyond_compare')}</span>
        {tooltip && (
          <ToolTipWrapper
            topTipPosition={isMobile ? TOP_TIP_POSITION.BOTTOM : TOP_TIP_POSITION.TOP}
            className={styles.productPriceComparision__titleWrapper__infoIcon}
            toolTipContentRenderer={() => tooltip}
            toolTipContentClassName={styles.productPriceComparision__titleWrapper__toolTipContentWrapper}
          >
            <IButtonIconContainer />
          </ToolTipWrapper>
        )}
      </div>
      <table>
        <thead>
          <tr className={styles.productPriceComparision__headerRow}>
            <td></td>
            <th
              className={`${styles.productPriceComparision__headerRow__cell} ${styles.productPriceComparision__firstCell}`}
            >
              <div className={styles.productPriceComparision__headerRow__cell__textContainer}>{t('quince')}</div>
            </th>
            {(() => {
              // Calculate maxLength once based on device type and product count
              const productCount = productComparisionsBrands?.length ?? 0
              const maxLength = (() => {
                if (isMobile) {
                  // Mobile: max 2 products, so we have more space
                  return productCount === 1 ? 15 : 9
                } else if (productCount === 1) {
                  // Desktop: 1 product
                  return 19
                } else if (productCount === 2) {
                  // Desktop: 2 products
                  return 11
                } else {
                  // Desktop: 3+ products
                  return 7
                }
              })()

              // First pass: Calculate display names and check if any brand in the row has multi-line text
              const brandDisplayData =
                productComparisionsBrands?.map(br => {
                  const brandName = getDisplayKey(br, 'brandDisplayName', br.brand, isNewLocalisedFlow)

                  // Check word count and length
                  // If brand name contains "&", treat it as a single word brand
                  const hasAmpersand = brandName.includes('&')
                  const words = hasAmpersand ? [brandName] : brandName.split(' ')

                  // Helper functions to reduce repetition
                  const shouldTruncate = (word: string) => word.length > maxLength && word.length - maxLength > 2
                  const truncateWord = (word: string) => `${word.substring(0, maxLength)}...`
                  const handleWordTruncation = (firstWord: string, secondWord: string) => {
                    if (shouldTruncate(firstWord)) {
                      return { displayName: truncateWord(firstWord), isTruncated: true }
                    } else if (shouldTruncate(secondWord)) {
                      return { displayName: `${firstWord}\n${truncateWord(secondWord)}`, isTruncated: true }
                    }
                    return null
                  }

                  let displayName = brandName
                  let isTruncated = false

                  if (words.length === 1) {
                    // Single word brand logic
                    if (shouldTruncate(brandName)) {
                      displayName = truncateWord(brandName)
                      isTruncated = true
                    }
                  } else if (words.length > 1) {
                    // Multi-word brand name logic
                    if (brandName.length <= maxLength) {
                      // Total fits within maxLength → display on single line
                      displayName = brandName
                    } else {
                      const firstWord = words[0] ?? ''
                      const secondWord = words[1] ?? ''
                      const isThreeOrMoreWords = words.length > 2

                      // Try truncation logic first (common for both 2+ words)
                      const truncationResult = handleWordTruncation(firstWord, secondWord)
                      if (truncationResult) {
                        displayName = truncationResult.displayName
                        isTruncated = truncationResult.isTruncated
                      } else {
                        // No truncation needed - handle based on word count
                        if (isThreeOrMoreWords) {
                          // 3+ words: Both first two words fit → display "firstword\nsecondword..."
                          displayName = `${firstWord}\n${secondWord}...`
                          isTruncated = true
                        } else {
                          // 2 words: Both words fit individually → display on separate lines
                          displayName = `${firstWord}\n${secondWord}`
                        }
                      }
                    }
                  }

                  return {
                    brand: br,
                    brandName,
                    words,
                    displayName,
                    isTruncated,
                    isMultiLine: displayName.includes('\n'),
                  }
                }) ?? []

              // Check if any brand in the row has multi-line text
              const hasAnyMultiLineInRow = brandDisplayData.some(data => data.isMultiLine)

              // Second pass: Render the brands with consistent tooltip positioning
              return brandDisplayData.map(({ brand: br, brandName, displayName, isTruncated, isMultiLine }) => {
                const textContainerClass = isMultiLine
                  ? `${styles.productPriceComparision__headerRow__cell__textContainer} ${styles.productPriceComparision__headerRow__cell__textContainer__multiLine}`
                  : `${styles.productPriceComparision__headerRow__cell__textContainer} ${styles.productPriceComparision__headerRow__cell__textContainer__singleLine}`

                // Determine tooltip position and CSS class
                // If ANY brand in the row is displayed in multiple lines (with <br>), ALL tooltips use multi-word CSS for consistency
                const tooltipPosition = TOP_TIP_POSITION.BOTTOM
                const tooltipClassName = hasAnyMultiLineInRow
                  ? `${styles.productPriceComparision__brandTooltip} ${styles.productPriceComparision__brandTooltip__multiWord}`
                  : styles.productPriceComparision__brandTooltip

                return (
                  <th className={styles.productPriceComparision__headerRow__cell} key={br.brand}>
                    <div className={textContainerClass}>
                      {isTruncated ? (
                        <ToolTipWrapper
                          topTipPosition={tooltipPosition}
                          toolTipContentRenderer={() => brandName}
                          toolTipContentClassName={tooltipClassName}
                        >
                          <span>{displayName}</span>
                        </ToolTipWrapper>
                      ) : (
                        <span>{displayName}</span>
                      )}
                    </div>
                  </th>
                )
              })
            })()}
          </tr>
        </thead>
        <tbody>
          <tr className={styles.productPriceComparision__bodyRow}>
            <th
              className={classNames(
                styles.productPriceComparision__bodyRow__titleCell,
                styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
              )}
            >
              {t('price')}
            </th>
            <td
              className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
            >
              <FormattedPrice amount={quincePrice} />
            </td>
            {productComparisionsBrands?.map(br => (
              <td
                className={classNames(
                  styles.productPriceComparision__bodyRow__cell,
                  styles.productPriceComparision__bodyRow__cell_newDesignV2,
                )}
                key={br.brand}
              >
                <FormattedPrice amount={br.prices[0]?.price} />
              </td>
            ))}
          </tr>

          <ComparisonRow
            isVisible={!!whiteGloveDeliveryAttribute}
            quinceField={tCommon('free')}
            attributeKey={WGD_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={whiteGloveDeliveryAttribute?.name ?? WGD_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!membershipFeeAttribute}
            quinceField={tCommon('free')}
            attributeKey={MF_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={membershipFeeAttribute?.name ?? MF_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!furnitureCoversAttribute}
            quinceField={tCommon('free')}
            attributeKey={FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={furnitureCoversAttribute?.name ?? FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!servingsAttribute}
            quinceField={servings}
            attributeKey={SERVING_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={
              isDogFood ? tCommon('ounces') : (servingsAttribute?.name ?? SERVING_PC_ADDITONAL_ATTRIBUTE_NAME)
            }
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!pricePerServingAttribute}
            quinceField={pricePerServing ? <FormattedPrice amount={pricePerServing} /> : ''}
            attributeKey={PPS_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={
              isDogFood
                ? tCommon('price_per_ounce')
                : (pricePerServingAttribute?.name ?? PPS_PC_ADDITONAL_ATTRIBUTE_NAME)
            }
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!overnightShippingAttribute}
            quinceField={tCommon('free')}
            attributeKey={OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME}
            attributeName={overnightShippingAttribute?.name ?? OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />

          {showGiftCardCredit ? (
            <>
              <tr className={styles.productPriceComparision__bodyRow}>
                <th
                  className={classNames(
                    styles.productPriceComparision__bodyRow__titleCell,
                    styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                  )}
                >
                  {t('store_credit_offer')}
                </th>
                <td
                  className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
                >
                  ${giftCardInformation?.giftCardCredit?.toFixed(2) ?? ''}
                </td>
                {giftCardInformation?.productComparisionsBrands?.map(br => (
                  <td
                    className={classNames(
                      styles.productPriceComparision__bodyRow__cell,
                      styles.productPriceComparision__bodyRow__cell_newDesignV2,
                    )}
                    key={br.brand}
                  >
                    ${br.prices[0]?.giftCardCredit?.toFixed(2) ?? 0}
                  </td>
                ))}
              </tr>
              {hideSavings ? null : (
                <tr className={styles.productPriceComparision__bodyRow}>
                  <th
                    className={classNames(
                      styles.productPriceComparision__bodyRow__titleCell,
                      styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                    )}
                  >
                    {tCommon('savings')}
                  </th>
                  <td
                    className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
                  >
                    <YellowCircleContainer />
                  </td>
                  {giftCardInformation?.productComparisionsBrands?.map(br => {
                    const competitorPrice = br.prices[0]?.price
                    const savings =
                      competitorPrice && competitorPrice > 0
                        ? Math.floor(((competitorPrice - effectiveQuincePrice) / competitorPrice) * 100)
                        : 0
                    return (
                      <td
                        className={classNames(
                          styles.productPriceComparision__bodyRow__cell,
                          styles.productPriceComparision__bodyRow__cell_newDesignV2,
                        )}
                        key={br.brand}
                      >
                        <p>{`${savings}%`}</p>
                      </td>
                    )
                  })}
                </tr>
              )}
            </>
          ) : hideSavings ? null : (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th
                className={classNames(
                  styles.productPriceComparision__bodyRow__titleCell,
                  styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                )}
              >
                {tCommon('savings')}
              </th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {showPricePerUnit ? (
                <>
                  {productComparisionsBrands?.map(br => (
                    <td
                      className={classNames(
                        styles.productPriceComparision__bodyRow__cell,
                        styles.productPriceComparision__bodyRow__cell_newDesignV2,
                      )}
                      key={br.brand}
                    >
                      <p>{getVitaminSavingsPercentage(br, pricePerServing)}%</p>
                    </td>
                  ))}
                </>
              ) : (
                <>
                  {productComparisionsBrands?.map(br => (
                    <td
                      className={classNames(
                        styles.productPriceComparision__bodyRow__cell,
                        styles.productPriceComparision__bodyRow__cell_newDesignV2,
                      )}
                      key={br.brand}
                    >
                      <p>
                        {Math.floor(
                          (((br.prices[0]?.price ?? 0) +
                            getBrandsExtraPrice(br, WGD_PC_ADDITONAL_ATTRIBUTE_NAME) +
                            getBrandsExtraPrice(br, FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME) +
                            getBrandsExtraPrice(br, MF_PC_ADDITONAL_ATTRIBUTE_NAME) +
                            getBrandsExtraPrice(br, OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME) -
                            quincePrice) *
                            100) /
                            ((br.prices[0]?.price ?? 0) +
                              getBrandsExtraPrice(br, WGD_PC_ADDITONAL_ATTRIBUTE_NAME) +
                              getBrandsExtraPrice(br, FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME) +
                              getBrandsExtraPrice(br, MF_PC_ADDITONAL_ATTRIBUTE_NAME) +
                              getBrandsExtraPrice(br, OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME)),
                        )}
                        %
                      </p>
                    </td>
                  ))}
                </>
              )}
            </tr>
          )}

          {showDollarComparisonPrice && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th
                className={classNames(
                  styles.productPriceComparision__bodyRow__titleCell,
                  styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                )}
              >
                {tCommon('savings_v1')}
              </th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {productComparisionsBrands?.map(br => (
                <td
                  className={classNames(
                    styles.productPriceComparision__bodyRow__cell,
                    styles.productPriceComparision__bodyRow__cell_newDesignV2,
                  )}
                  key={br.brand}
                >
                  <p>
                    <FormattedPrice amount={(br.prices[0]?.price ?? 0) - quincePrice} />
                  </p>
                </td>
              ))}
            </tr>
          )}

          {(
            productComparisionsBrands?.[0]?.additionalProperties
              ?.filter(
                item =>
                  item.key != MF_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.key != WGD_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.key != SERVING_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.key != PPS_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.key != FURNITURE_COVERS_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.key != OVERNIGHT_SHIPPING_PC_ADDITONAL_ATTRIBUTE_NAME,
              )
              .map(ap => ap.name) ?? []
          ).map((apName: string) => {
            return (
              <tr className={styles.productPriceComparision__bodyRow} key={apName}>
                <th
                  className={classNames(
                    styles.productPriceComparision__bodyRow__titleCell,
                    styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                  )}
                >
                  {apName}
                </th>
                <td
                  className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
                >
                  <YellowCircleContainer />
                </td>
                {productComparisionsBrands?.map(br => (
                  <td
                    className={classNames(
                      styles.productPriceComparision__bodyRow__cell,
                      styles.productPriceComparision__bodyRow__cell_newDesignV2,
                    )}
                    key={br.brand}
                  >
                    <AdditionalPropertyComponent additionalProperties={br.additionalProperties} apName={apName} />
                  </td>
                ))}
              </tr>
            )
          })}

          {!(overnightShippingAttribute || whiteGloveDeliveryAttribute) && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th
                className={classNames(
                  styles.productPriceComparision__bodyRow__titleCell,
                  styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                )}
              >
                {t('free_shipping')}
              </th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {productComparisionsBrands?.map(br => (
                <td
                  className={classNames(
                    styles.productPriceComparision__bodyRow__cell,
                    styles.productPriceComparision__bodyRow__cell_newDesignV2,
                  )}
                  key={br.brand}
                >
                  {br.freeShipping ? <GrayCircleContainer /> : <CrossIconContainer />}
                </td>
              ))}
            </tr>
          )}
          {!hideFreeReturns && !whiteGloveDeliveryAttribute && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th
                className={classNames(
                  styles.productPriceComparision__bodyRow__titleCell,
                  styles.productPriceComparision__bodyRow__titleCell_newDesignV2,
                )}
              >
                {hasRestockingFee ? tCommon('easy_returns') : tCommon('free_returns_v1')}
              </th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__bodyRow__cell_newDesignV2} ${styles.productPriceComparision__firstCell} ${styles.productPriceComparision__bodyRow__cell__yearLongReturns}`}
              >
                {t('365_days')}
              </td>
              {productComparisionsBrands?.map(br => (
                <td
                  className={classNames(
                    styles.productPriceComparision__bodyRow__cell,
                    styles.productPriceComparision__bodyRow__cell_newDesignV2,
                  )}
                  key={br.brand}
                >
                  {br.freeReturns === 0 ? tCommon('final_sale') : tCommon('var1_days', { var1: br.freeReturns })}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default React.memo(ProductPriceComparision)
